// Days and shifts, said plainly (ADR-0029; spec/13 §10). Two things the venue used to have to open by hand, made
// legible:
//
//   - the **business day** runs by itself, cutoff to cutoff: nobody opens it. It ends either by a manager closing it
//     (`shifts.dayCloseMode: manual`, the default) or by rolling over at the cutoff (`auto`, for 24×7 venues), and the
//     strip says which and when;
//   - a **shift** is who holds the cash drawer: started with a counted float, handed over with a count, ended with a
//     final count. The strip says who holds it, since when, with what float — or that nobody does.
//
// `DayStatusStrip` is the persistent line on the Sell and Shifts screens; `DaysAndShiftsHelp` the short explainer it
// (and the settings group) opens. Both read `GET …/business-day`; the server owns every fact, this only says it.

import type { BusinessDayStatus } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, cx, Dialog } from '@clavian/ui-web';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { useAccess } from '../../api/queries';
import { Icon, NavIcon } from '../../shell/nav-icons';
import type { MyVenue } from '../../api/me';
import { useBusinessDayStatus } from './queries';

import './days-and-shifts.css';

const SHIFTS_OWN = 'shifts:own';
const SHIFTS_DAY_CLOSE = 'shifts:day_close';
const SETTINGS_PAYMENTS = 'settings:payments';

/** `Wed 1 Oct` for a venue-local date (`YYYY-MM-DD`): a calendar date, so formatted in UTC where it is that date. */
export function localDateLabel(day: string, locale: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return new Intl.DateTimeFormat(locale, { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' }).format(date);
}

/** The calendar day before `day` (`YYYY-MM-DD`), by date arithmetic in UTC — no timezone can move it. */
export function previousDay(day: string): string {
  const date = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return day;
  return new Date(date.getTime() - 86_400_000).toISOString().slice(0, 10);
}

/** The venue's business day now, for any screen that needs it; undefined while it loads or for a role without it. */
export function useVenueBusinessDay(venue: Pick<MyVenue, 'id' | 'organizationId'>): BusinessDayStatus | undefined {
  return useBusinessDayStatus(venue.organizationId, venue.id).data;
}

/**
 * The persistent strip (ADR-0029): "Business day: Wed 1 Oct · rolls over at 06:00 (automatic)" and "Shift: Riya · since
 * 14:02 · float ₹2,000" — or "No shift — Start shift to take cash" with the button that does it, where the screen
 * offers one. Days a manager must act on (an automatic close to sign off, or yesterday still open under manual close)
 * show as a chip to whom may close days.
 */
export function DayStatusStrip({
  venue,
  onStartShift,
}: {
  readonly venue: Pick<MyVenue, 'id' | 'organizationId' | 'slug'>;
  /** Offered when no shift is open and the venue tracks drawers: the screen opens its Start shift dialog. */
  readonly onStartShift?: () => void;
}): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const access = useAccess({ venueId: venue.id });
  const status = useBusinessDayStatus(venue.organizationId, venue.id);
  const [helping, setHelping] = useState(false);
  const data = status.data;
  if (data === undefined) return null;

  const canOwn = access.status === 'ready' && access.can(SHIFTS_OWN);
  const canDayClose = access.status === 'ready' && access.can(SHIFTS_DAY_CLOSE);
  const dayLabel = localDateLabel(data.businessDay, format.locale);
  const rollsAt = format.time(data.rollsOverAt);
  const custody = data.custody;
  const spanning = custody !== null && custody.businessDay !== data.businessDay;
  const drawers = data.drawers;
  const namesOf = (drawer: (typeof drawers)[number]) =>
    (drawer.members.length > 0 ? drawer.members.map((member) => member.name ?? t('shifts.holderUnknown')) : [drawer.openedByName ?? t('shifts.holderUnknown')]).join(', ');
  // ADR-0029: does the caller hold a drawer? If not — and the venue gives each person their own — Start shift stays offered.
  const holdsOne = access.status === 'ready' && drawers.some((drawer) => drawer.members.some((member) => member.memberId === access.memberId) || drawer.openedByMemberId === access.memberId);
  const mayStart = data.shiftsEnabled && !holdsOne && (drawers.length === 0 || data.cashHandling !== 'shared_drawer');

  return (
    <section
      className="console-daystrip console-rail"
      data-rail={!data.shiftsEnabled ? undefined : custody !== null || drawers.length > 0 ? 'live' : 'warn'}
      aria-label={t('shifts.strip.label')}
    >
      <div className="console-daystrip__item">
        <span className="console-daystrip__icon" aria-hidden="true">
          <Icon name="clock" />
        </span>
        <span className="console-daystrip__text">
          <strong>{t('shifts.strip.day', { day: dayLabel })}</strong>
          <span className="console-daystrip__sub">
            {t('shifts.strip.rolls', { time: rollsAt })} ({data.dayCloseMode === 'auto' ? t('shifts.strip.auto') : t('shifts.strip.manual')})
          </span>
        </span>
      </div>

      <span className="console-daystrip__rule" aria-hidden="true" />

      <div className={cx('console-daystrip__item', custody !== null && 'console-daystrip__item--held')}>
        <span className="console-daystrip__icon" aria-hidden="true">
          <Icon name="cash" />
        </span>
        <span className="console-daystrip__text">
          {!data.shiftsEnabled ? (
            <span>{t('shifts.strip.notTracked')}</span>
          ) : drawers.length > 1 ? (
            // Several counters at once (ADR-0029): "Counter 1: Riya since 14:02 · Counter 2: Aman since 18:00".
            <strong>
              {drawers
                .map((drawer) => t('shifts.strip.drawer', { counter: drawer.counterName ?? t('shifts.noCounter'), names: namesOf(drawer), time: format.time(drawer.openedAt) }))
                .join(' · ')}
            </strong>
          ) : custody === null ? (
            <span>{t('shifts.strip.noShift')}</span>
          ) : (
            <>
              <strong>
                {custody.members.length > 1
                  ? t('shifts.strip.shared', { names: namesOf(custody), time: format.time(custody.openedAt), float: format.money(custody.openingFloat, 'noFraction') })
                  : t('shifts.strip.shift', {
                      name: custody.openedByName ?? t('shifts.holderUnknown'),
                      time: format.time(custody.openedAt),
                      float: format.money(custody.openingFloat, 'noFraction'),
                    })}
              </strong>
              {spanning ? <span className="console-daystrip__sub">{t('shifts.spans', { day: localDateLabel(custody.businessDay, format.locale) })}</span> : null}
            </>
          )}
        </span>
      </div>

      <div className="console-daystrip__actions">
        {canDayClose && data.awaitingSignOff.length > 0 ? (
          <Link
            to="/v/$venueSlug/shifts"
            params={{ venueSlug: venue.slug }}
            search={{ day: data.awaitingSignOff[0] }}
            className="console-daystrip__chip"
          >
            {t('shifts.strip.signOff', { count: data.awaitingSignOff.length })}
          </Link>
        ) : null}
        {canDayClose && data.dayCloseMode === 'manual' && !data.previousDayClosed ? (
          <Link to="/v/$venueSlug/shifts" params={{ venueSlug: venue.slug }} search={{ day: previousDay(data.businessDay) }} className="console-daystrip__chip">
            {t('shifts.strip.previousOpen')}
          </Link>
        ) : null}
        {onStartShift !== undefined && canOwn && mayStart ? (
          <Button variant="primary" size="sm" onClick={onStartShift}>
            {t('shifts.startShort')}
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" icon={<Icon name="info" />} onClick={() => setHelping(true)}>
          {t('shifts.strip.help')}
        </Button>
      </div>
      <DaysAndShiftsHelp open={helping} onOpenChange={setHelping} status={data} venueSlug={venue.slug} />
    </section>
  );
}

/**
 * "How days and shifts work" (ADR-0029): the business day, the two ways it ends (the venue's own first), the drawer, and
 * what a 24×7 venue should choose. Links to the settings for a member who may change them.
 */
export function DaysAndShiftsHelp({
  open,
  onOpenChange,
  status,
  venueSlug,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly status: Pick<BusinessDayStatus, 'cutoff' | 'dayCloseMode'> & { readonly venueId?: string; readonly cashHandling?: BusinessDayStatus['cashHandling'] };
  readonly venueSlug: string;
}): ReactNode {
  const { t } = useTranslation('console');
  const access = useAccess(status.venueId === undefined ? undefined : { venueId: status.venueId });
  const canEdit = access.status === 'ready' && access.can(SETTINGS_PAYMENTS);
  const cutoff = status.cutoff;
  const manual = (
    <li key="manual" className={cx('console-dayhelp__mode', status.dayCloseMode === 'manual' && 'console-dayhelp__mode--current')}>
      <strong>{t('shifts.help.closeManualTitle')}</strong>
      <span>{t('shifts.help.closeManual')}</span>
    </li>
  );
  const auto = (
    <li key="auto" className={cx('console-dayhelp__mode', status.dayCloseMode === 'auto' && 'console-dayhelp__mode--current')}>
      <strong>{t('shifts.help.closeAutoTitle')}</strong>
      <span>{t('shifts.help.closeAuto', { cutoff })}</span>
    </li>
  );
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('shifts.help.title')}
      description={t('shifts.help.intro')}
      className="console-dayhelp"
      footer={
        <>
          {canEdit ? (
            <Button variant="secondary" asChild>
              <Link to="/v/$venueSlug/settings/$group" params={{ venueSlug, group: 'cashAndShifts' }} onClick={() => onOpenChange(false)}>
                {t('shifts.help.settings')}
              </Link>
            </Button>
          ) : null}
          <Button variant="primary" onClick={() => onOpenChange(false)}>
            {t('shifts.help.done')}
          </Button>
        </>
      }
    >
      <div className="console-dayhelp__body">
        <section className="console-dayhelp__block">
          <h3 className="console-dayhelp__title">
            <NavIcon name="shifts" /> {t('shifts.help.dayTitle')}
          </h3>
          <p>{t('shifts.help.day', { cutoff })}</p>
          <ul className="console-dayhelp__modes">{status.dayCloseMode === 'auto' ? [auto, manual] : [manual, auto]}</ul>
        </section>
        <section className="console-dayhelp__block">
          <h3 className="console-dayhelp__title">
            <Icon name="cash" /> {t('shifts.help.shiftTitle')}
          </h3>
          <ol className="console-dayhelp__steps">
            <li>{t('shifts.help.shiftStart')}</li>
            <li>{t('shifts.help.shiftHandOver')}</li>
            <li>{t('shifts.help.shiftEnd')}</li>
          </ol>
        </section>
        <section className="console-dayhelp__block">
          <h3 className="console-dayhelp__title">
            <NavIcon name="staff" /> {t('shifts.help.peopleTitle')}
          </h3>
          <ul className="console-dayhelp__modes">
            {(['per_drawer', 'shared_drawer', 'floor_without_drawer'] as const)
              .slice()
              .sort((a, b) => (a === status.cashHandling ? -1 : b === status.cashHandling ? 1 : 0))
              .map((mode) => (
                <li key={mode} className={cx('console-dayhelp__mode', mode === (status.cashHandling ?? 'per_drawer') && 'console-dayhelp__mode--current')}>
                  <span>{t(`shifts.help.${mode === 'per_drawer' ? 'perDrawer' : mode === 'shared_drawer' ? 'sharedDrawer' : 'floorWithoutDrawer'}`)}</span>
                </li>
              ))}
          </ul>
        </section>
        <section className="console-dayhelp__block console-dayhelp__block--tip">
          <h3 className="console-dayhelp__title">
            <Icon name="clock" /> {t('shifts.help.allDayTitle')}
          </h3>
          <p>{t('shifts.help.allDay')}</p>
        </section>
      </div>
    </Dialog>
  );
}
